import { createSerializer } from "nuqs";
import { DEFAULT_STATUS } from "@/features/attorney/views/Entries/filters/defaults";
import type { EntryFilterState } from "@/features/attorney/views/Entries/filters/types";
import { filterStateParser } from "@/features/attorney/views/Entries/filters/urlState";
import { viewToRoute } from "@/shared/config/routes";

export type StatusView = "entries" | "calendar" | "reporting";

const baseStatusSubtabs = [
	{ id: "tracked", label: "Tracked", values: ["tracked", "reviewed"] },
	{ id: "exported", label: "Exported", values: ["exported"] },
	{ id: "deleted", label: "Deleted", values: ["deleted"] },
] as const;

const trackedExportedSubtab = {
	id: "tracked-exported",
	label: "Tracked & Exported",
	values: ["tracked", "reviewed", "exported"],
} as const;

export type StatusSubtab = {
	id: string;
	label: string;
	values: readonly string[];
};

const serializeFilters = createSerializer({ filter: filterStateParser });

export function getStatusSubtabs(): StatusSubtab[] {
	return [trackedExportedSubtab, ...baseStatusSubtabs];
}

export function getCurrentFilters(search: string): EntryFilterState {
	const params = new URLSearchParams(search);
	return filterStateParser.parse(params.get("filter") ?? "") ?? { status: [] };
}

export function getSelectedStatusValues(search: string): string[] {
	return getCurrentFilters(search).status ?? [...DEFAULT_STATUS];
}

export function getActiveStatus(
	search: string,
	subtabs: readonly StatusSubtab[],
): string | null {
	const selected = getSelectedStatusValues(search);
	return (
		subtabs.find(
			(subtab) =>
				subtab.values.length === selected.length &&
				subtab.values.every((value) => selected.includes(value)),
		)?.id ?? null
	);
}

export function buildStatusUrl(
	view: StatusView,
	search: string,
	subtab: StatusSubtab,
): string {
	const params = new URLSearchParams(search);
	params.delete("status"); // Retire Reporting's former, separate status control.
	const remainingSearch = params.toString();
	return serializeFilters(
		`${viewToRoute[view]}${remainingSearch ? `?${remainingSearch}` : ""}`,
		{
			filter: {
				...getCurrentFilters(search),
				status: [...subtab.values],
			},
		},
	);
}
